import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { formatDate } from '../../utils/dateUtils';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 40px 20px;
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 60px 40px;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 20px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  margin: 0;
  opacity: 0.9;
  font-weight: 300;
`;

const Content = styled.div`
  padding: 60px 40px;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const LastUpdated = styled.div`
  background: #e8f4fd;
  border-left: 4px solid #667eea;
  padding: 20px;
  margin-bottom: 40px;
  border-radius: 0 10px 10px 0;
  
  p {
    margin: 0;
    color: #2c3e50;
    font-weight: 500;
  }
`;

const Section = styled.section`
  margin-bottom: 50px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 20px 0;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 2px;
  }
`;

const SubTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #34495e;
  margin: 30px 0 15px 0;
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #34495e;
  margin: 0 0 20px 0;
  text-align: justify;
`;

const List = styled.ul`
  margin: 20px 0;
  padding-left: 30px;
  
  li {
    font-size: 16px;
    line-height: 1.8;
    color: #34495e;
    margin-bottom: 10px;
  }
`;

const WarningBox = styled.div`
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 10px;
  padding: 20px;
  margin: 30px 0;
  
  h4 {
    color: #721c24;
    margin: 0 0 10px 0;
    font-size: 18px;
    font-weight: 600;
  }
  
  p {
    color: #721c24;
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
  }
`;

const ContactInfo = styled.div`
  background: #f8f9fa;
  border-radius: 15px;
  padding: 30px;
  margin: 40px 0;
  text-align: center;
  
  h3 {
    color: #2c3e50;
    margin: 0 0 20px 0;
  }
  
  p {
    margin: 10px 0;
    color: #34495e;
  }
  
  a {
    color: #667eea;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TermsPage: React.FC = () => {
  const currentDate = formatDate(new Date().toISOString());

  return (
    <PageContainer>
      <Helmet>
        <title>Điều khoản Sử dụng - Infonium | Quy định và Chính sách</title>
        <meta name="description" content="Điều khoản sử dụng dịch vụ Infonium - Tìm hiểu các quy định, chính sách và điều kiện khi sử dụng nền tảng thông tin của chúng tôi." />
        <meta name="keywords" content="điều khoản sử dụng, quy định, chính sách, terms of service, user agreement, legal terms" />
        <meta name="author" content="Infonium" />
        <meta property="og:title" content="Điều khoản Sử dụng - Infonium" />
        <meta property="og:description" content="Tìm hiểu các điều khoản và quy định khi sử dụng dịch vụ của Infonium - nền tảng thông tin hiện đại." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : 'https://www.infonium.org/terms'} />
        <meta property="og:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
        <meta property="og:site_name" content="Infonium" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Điều khoản Sử dụng - Infonium" />
        <meta name="twitter:description" content="Tìm hiểu các điều khoản và quy định khi sử dụng dịch vụ của Infonium - nền tảng thông tin hiện đại." />
        <meta name="twitter:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
        <meta name="twitter:site" content="@infonium" />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : 'https://www.infonium.org/terms'} />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="vi" />
      </Helmet>
      <ContentWrapper>
        <Hero>
          <HeroTitle>Điều khoản Sử dụng</HeroTitle>
          <HeroSubtitle>Quy định và điều kiện sử dụng dịch vụ</HeroSubtitle>
        </Hero>

        <Content>
          <LastUpdated>
            <p><strong>Cập nhật lần cuối:</strong> {currentDate}</p>
          </LastUpdated>

          <Section>
            <SectionTitle>1. Chấp nhận Điều khoản</SectionTitle>
            <Paragraph>
              Chào mừng bạn đến với Infonium. Bằng cách truy cập và sử dụng website này, 
              bạn đồng ý tuân thủ và bị ràng buộc bởi các điều khoản và điều kiện sử dụng 
              được nêu dưới đây. Nếu bạn không đồng ý với bất kỳ phần nào của các điều khoản này, 
              vui lòng không sử dụng website của chúng tôi.
            </Paragraph>
            <Paragraph>
              Các điều khoản này áp dụng cho tất cả người dùng website, bao gồm nhưng không 
              giới hạn ở người dùng chỉ xem nội dung, người đóng góp nội dung, và các tổ chức.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>2. Mô tả Dịch vụ</SectionTitle>
            <Paragraph>
              Infonium là một nền tảng thông tin trực tuyến cung cấp tin tức, bài viết, 
              và nội dung đa phương tiện cho người dùng. Chúng tôi cung cấp:
            </Paragraph>
            <List>
              <li>Tin tức và bài viết về các chủ đề đa dạng</li>
              <li>Nội dung âm thanh và phụ đề</li>
              <li>Giao diện tương tác và trải nghiệm người dùng tối ưu</li>
              <li>Dịch vụ tìm kiếm và phân loại nội dung</li>
              <li>Tính năng chia sẻ và tương tác xã hội</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>3. Quyền và Trách nhiệm của Người dùng</SectionTitle>
            
            <SubTitle>3.1 Quyền của Người dùng</SubTitle>
            <List>
              <li>Truy cập và sử dụng nội dung trên website miễn phí</li>
              <li>Chia sẻ nội dung thông qua các kênh mạng xã hội</li>
              <li>Gửi phản hồi và góp ý về dịch vụ</li>
              <li>Yêu cầu hỗ trợ kỹ thuật khi cần thiết</li>
            </List>

            <SubTitle>3.2 Trách nhiệm của Người dùng</SubTitle>
            <List>
              <li>Sử dụng website một cách hợp pháp và phù hợp</li>
              <li>Không vi phạm bản quyền hoặc quyền sở hữu trí tuệ</li>
              <li>Không sử dụng website cho mục đích bất hợp pháp</li>
              <li>Bảo mật thông tin đăng nhập (nếu có)</li>
              <li>Báo cáo các vấn đề bảo mật hoặc lạm dụng</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>4. Hành vi Bị cấm</SectionTitle>
            <Paragraph>
              Khi sử dụng website, bạn không được:
            </Paragraph>
            <List>
              <li>Sao chép, phân phối hoặc sử dụng nội dung vì mục đích thương mại mà không có sự cho phép</li>
              <li>Tấn công, hack hoặc cố gắng truy cập trái phép vào hệ thống</li>
              <li>Gửi virus, malware hoặc mã độc hại</li>
              <li>Spam hoặc gửi nội dung quảng cáo không mong muốn</li>
              <li>Mạo danh hoặc đại diện sai sự thật về danh tính</li>
              <li>Thu thập thông tin cá nhân của người dùng khác</li>
              <li>Sử dụng bot hoặc công cụ tự động để truy cập website</li>
            </List>

            <WarningBox>
              <h4>Cảnh báo:</h4>
              <p>
                Vi phạm các quy định trên có thể dẫn đến việc chấm dứt quyền truy cập 
                và có thể bị truy cứu trách nhiệm pháp lý.
              </p>
            </WarningBox>
          </Section>

          <Section>
            <SectionTitle>5. Quyền Sở hữu Trí tuệ</SectionTitle>
            <Paragraph>
              Tất cả nội dung trên website Infonium, bao gồm nhưng không giới hạn ở văn bản, 
              hình ảnh, âm thanh, video, logo, và thiết kế, đều được bảo vệ bởi luật bản quyền 
              và các luật sở hữu trí tuệ khác.
            </Paragraph>
            
            <SubTitle>5.1 Nội dung của Infonium</SubTitle>
            <List>
              <li>Bản quyền thuộc về Infonium hoặc các bên cấp phép</li>
              <li>Người dùng có thể xem và chia sẻ cho mục đích cá nhân, phi thương mại</li>
              <li>Nghiêm cấm sao chép, phân phối lại vì mục đích thương mại</li>
            </List>

            <SubTitle>5.2 Nội dung do Người dùng tạo</SubTitle>
            <Paragraph>
              Nếu bạn gửi nội dung cho chúng tôi (phản hồi, góp ý, v.v.), bạn cấp cho 
              Infonium quyền sử dụng, sửa đổi và hiển thị nội dung đó.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>6. Quảng cáo và Dịch vụ Bên thứ ba</SectionTitle>
            <Paragraph>
              Website của chúng tôi có thể hiển thị quảng cáo từ Google AdSense và các 
              mạng quảng cáo khác. Chúng tôi không chịu trách nhiệm về:
            </Paragraph>
            <List>
              <li>Nội dung của các quảng cáo</li>
              <li>Sản phẩm hoặc dịch vụ được quảng cáo</li>
              <li>Giao dịch giữa bạn và nhà quảng cáo</li>
              <li>Chính sách bảo mật của các website bên thứ ba</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>7. Tuyên bố Miễn trách</SectionTitle>
            <Paragraph>
              Website và nội dung được cung cấp "như hiện tại" mà không có bất kỳ bảo đảm nào. 
              Chúng tôi không đảm bảo:
            </Paragraph>
            <List>
              <li>Tính chính xác, đầy đủ hoặc kịp thời của thông tin</li>
              <li>Website sẽ hoạt động liên tục và không có lỗi</li>
              <li>Các lỗi sẽ được khắc phục</li>
              <li>Website không chứa virus hoặc thành phần có hại</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>8. Giới hạn Trách nhiệm</SectionTitle>
            <Paragraph>
              Trong phạm vi tối đa được pháp luật cho phép, Infonium không chịu trách nhiệm 
              về bất kỳ thiệt hại nào phát sinh từ việc sử dụng hoặc không thể sử dụng website, 
              bao gồm:
            </Paragraph>
            <List>
              <li>Thiệt hại trực tiếp, gián tiếp, ngẫu nhiên hoặc hậu quả</li>
              <li>Mất mát dữ liệu hoặc lợi nhuận</li>
              <li>Gián đoạn kinh doanh</li>
              <li>Thiệt hại do virus hoặc malware</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>9. Bồi thường</SectionTitle>
            <Paragraph>
              Bạn đồng ý bồi thường và giữ cho Infonium không bị thiệt hại từ bất kỳ 
              khiếu nại, tổn thất, thiệt hại, trách nhiệm pháp lý và chi phí (bao gồm 
              phí luật sư hợp lý) phát sinh từ:
            </Paragraph>
            <List>
              <li>Việc bạn sử dụng website</li>
              <li>Vi phạm các điều khoản này</li>
              <li>Vi phạm quyền của bên thứ ba</li>
              <li>Nội dung bạn gửi hoặc chia sẻ</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>10. Chấm dứt</SectionTitle>
            <Paragraph>
              Chúng tôi có quyền chấm dứt hoặc tạm ngừng quyền truy cập của bạn vào website 
              ngay lập tức, mà không cần thông báo trước, vì bất kỳ lý do nào, bao gồm 
              nhưng không giới hạn ở việc vi phạm các điều khoản này.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>11. Luật áp dụng</SectionTitle>
            <Paragraph>
              Các điều khoản này được điều chỉnh bởi và được giải thích theo pháp luật 
              Việt Nam. Mọi tranh chấp phát sinh sẽ được giải quyết tại các tòa án 
              có thẩm quyền tại Việt Nam.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>12. Thay đổi Điều khoản</SectionTitle>
            <Paragraph>
              Chúng tôi có quyền sửa đổi các điều khoản này bất kỳ lúc nào. Các thay đổi 
              sẽ có hiệu lực ngay khi được đăng tải trên website. Việc bạn tiếp tục sử dụng 
              website sau khi có thay đổi đồng nghĩa với việc bạn chấp nhận các điều khoản mới.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>13. Thông tin Liên hệ</SectionTitle>
            <Paragraph>
              Nếu bạn có bất kỳ câu hỏi nào về các điều khoản này, vui lòng liên hệ với chúng tôi.
            </Paragraph>
          </Section>

          <ContactInfo>
            <h3>Liên hệ với chúng tôi</h3>
            <p>Nếu bạn có câu hỏi về điều khoản sử dụng, vui lòng liên hệ:</p>
            <p><strong>Email:</strong> <a href="mailto:contact@infonium.org">contact@infonium.org</a></p>
          </ContactInfo>
        </Content>
      </ContentWrapper>
    </PageContainer>
  );
};

export default TermsPage; 